/**
 * AgendamentoTab - Scope scheduling management (Tab 2)
 *
 * Features:
 * - Status cards (clickable, toggle filter)
 * - Table with scope items
 * - Agendar modal (date within month only)
 * - Ver Detalhes modal (read-only)
 * - Upload escopo modal (with replace warning)
 * - Export Excel
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Building2, Calendar, Upload, Download, Eye, Edit3, Trash2, X, FileSpreadsheet, Loader2, Info, Plus } from 'lucide-react';
import {
  MantineReactTable,
  useMantineReactTable,
  type MRT_ColumnDef,
} from 'mantine-react-table';
import escopoContagemService, {
  type AgendamentoItem,
  type AgendamentoListResponse,
  type EscopoMes,
  type DropdownOption,
  type UploadResult,
} from '../../services/escopoContagemService';
import clienteService, { type ValidarIntervaloResponse } from '../../services/clienteService';
import IntervaloAgendamentoModal, { type JustificativaIntervalo } from '../IntervaloAgendamentoModal';
import { msg } from '../../utils/messages';
// N133 (Alana): buscar hospital por nome, CNPJ ou código também na inclusão
// manual. A regra vem do mesmo util que o botão "Vincular" (N114) usa.
import { ComboboxSelector } from '../ui/ComboboxSelector';
import { opcoesHospitalBuscavel } from '../../utils/hospitalOptions';
import PermissionGate from '../PermissionGate';
import { useRefetchOnFocus } from '../../hooks/useRefetchOnFocus';
import useAuthStore from '../../stores/authStore';
import HospitalConsultaModal from './HospitalConsultaModal';

interface AgendamentoTabProps {
  escopo: EscopoMes;
  onDataChanged?: () => void;
  refreshKey?: number;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
  NAO_AGENDADO: { label: 'Não Agendado', color: 'text-gray-600 dark:text-gray-400', bgColor: 'bg-gray-100 dark:bg-gray-700' },
  PRE_AGENDADA: { label: 'Pré-Agendada', color: 'text-yellow-700 dark:text-yellow-400', bgColor: 'bg-yellow-100 dark:bg-yellow-900/30' },
  AGENDADA: { label: 'Agendada', color: 'text-blue-700 dark:text-blue-400', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
  // T21: jobs seguem para CONFIRMADA/REAGENDADA depois de agendados — sem
  // estas entradas o fallback renderizava "Não Agendado" (com data e tudo).
  CONFIRMADA: { label: 'Confirmada', color: 'text-indigo-700 dark:text-indigo-400', bgColor: 'bg-indigo-100 dark:bg-indigo-900/30' },
  REAGENDADA: { label: 'Reagendada', color: 'text-cyan-700 dark:text-cyan-400', bgColor: 'bg-cyan-100 dark:bg-cyan-900/30' },
  EM_EXECUCAO: { label: 'Em Execução', color: 'text-orange-700 dark:text-orange-400', bgColor: 'bg-orange-100 dark:bg-orange-900/30' },
  EXECUTADA: { label: 'Executada', color: 'text-green-700 dark:text-green-400', bgColor: 'bg-green-100 dark:bg-green-900/30' },
  NAO_EXECUTADA: { label: 'Não Executada', color: 'text-rose-700 dark:text-rose-400', bgColor: 'bg-rose-100 dark:bg-rose-900/30' },
  // N21c: negativa do calendário — sem esta entrada o fallback renderizava
  // "Não Agendado" para contagem suspensa pelo cliente (caso Carmini Caricchi).
  NAO_AUTORIZADA: { label: 'Não Autorizada', color: 'text-orange-700 dark:text-orange-400', bgColor: 'bg-orange-100 dark:bg-orange-900/30' },
  CANCELADA: { label: 'Cancelada', color: 'text-red-700 dark:text-red-400', bgColor: 'bg-red-100 dark:bg-red-900/30' },
};

// ⛔ N179 — ESPELHO de `STATUS_BUCKET` em `inventory/api_escopo_contagem.py`.
// Os cards vêm contados por bucket pelo backend; o filtro da tabela precisa
// agrupar igual, senão o número do card não bate com as linhas que ele mostra.
// Se mexer num lado, mexa no outro.
const STATUS_BUCKET: Record<string, string> = {
  NAO_AGENDADO: 'NAO_AGENDADO',
  PRE_AGENDADA: 'PRE_AGENDADA',
  AGENDADA: 'AGENDADA',
  CONFIRMADA: 'AGENDADA',
  REAGENDADA: 'AGENDADA',
  EM_EXECUCAO: 'EM_EXECUCAO',
  EXECUTADA: 'EXECUTADA',
  NAO_EXECUTADA: 'CANCELADA',
  // ⛔ N180 (Alana, 26/08): balde PRÓPRIO. O card "Cancelada" mostrava 7
  // enquanto o breakdown do topo mostrava "Não Autorizada 6" + "Cancelada 1".
  // Ela FATURA por esses status ("uma cancelada eu cobro" — N179), então
  // somar os dois joga 6 idas não autorizadas no balde cobrado.
  NAO_AUTORIZADA: 'NAO_AUTORIZADA',
  CANCELADA: 'CANCELADA',
};

function formatDate(d: string | null): string {
  if (!d) return '—';
  const parts = d.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}`;
  return d;
}

function formatTime(t: string | null): string {
  if (!t) return '';
  return t.substring(0, 5);
}

export default function AgendamentoTab({ escopo, onDataChanged, refreshKey }: AgendamentoTabProps) {
  const isMaster = useAuthStore(state => state.user?.role === 'MASTER');

  const [data, setData] = useState<AgendamentoListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeStatusFilter, setActiveStatusFilter] = useState<string | null>(null);
  const [hospitalConsulta, setHospitalConsulta] = useState<AgendamentoItem | null>(null);

  // Filters
  const [filterAuditor, setFilterAuditor] = useState<number | ''>('');
  const [filterCliente, setFilterCliente] = useState<number | ''>('');
  const [filterHospital, setFilterHospital] = useState<number | ''>('');

  // Dropdown data
  const [auditoresList, setAuditoresList] = useState<{id: number; nome: string}[]>([]);
  const [clientesList, setClientesList] = useState<{id: number; nome: string}[]>([]);
  const [hospitaisList, setHospitaisList] = useState<{id: number; nome: string}[]>([]);

  // Modals
  const [agendarItem, setAgendarItem] = useState<AgendamentoItem | null>(null);
  const [detalhesItem, setDetalhesItem] = useState<AgendamentoItem | null>(null);
  const [editItem, setEditItem] = useState<AgendamentoItem | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [showInfoModal, setShowInfoModal] = useState(false);

  // Agendar form
  const [agendarData, setAgendarData] = useState('');
  const [agendarInicio, setAgendarInicio] = useState('07:00');
  const [agendarFim, setAgendarFim] = useState('09:00');
  const [agendando, setAgendando] = useState(false);
  // Multi-cliente / multi-auditor no Agendar (mesmo hospital)
  const [agendarSiblings, setAgendarSiblings] = useState<AgendamentoItem[]>([]);
  const [agendarItemIds, setAgendarItemIds] = useState<number[]>([]); // itens (clientes) marcados
  const [agendarAuditorIds, setAgendarAuditorIds] = useState<number[]>([]); // auditores marcados
  const [agendarAuditoresHospital, setAgendarAuditoresHospital] = useState<{ id: number; nome: string }[]>([]);

  // Abre o modal Agendar: descobre clientes-irmaos do MESMO hospital (NAO_AGENDADO)
  // e os auditores vinculados ao hospital.
  const openAgendarModal = useCallback(async (item: AgendamentoItem) => {
    setAgendarItem(item);
    setAgendarData('');
    setAgendarInicio('07:00');
    setAgendarFim('09:00');
    // ⛔ N179 (correção, 24/08): "irmão" é o cliente do mesmo hospital cuja vaga
    // está AGENDÁVEL — e vaga ocupada por contagem morta (cancelada / não
    // executada / não autorizada) está livre na prática, é o caso do
    // reagendamento. Filtrar só por NAO_AGENDADO escondia esses clientes do
    // checkbox e obrigava a agendar um por um.
    const AGENDAVEL = ['NAO_AGENDADO', 'CANCELADA', 'NAO_EXECUTADA', 'NAO_AUTORIZADA'];
    const siblings = (data?.itens || []).filter(
      i => i.hospital_id === item.hospital_id && AGENDAVEL.includes(i.status)
    );
    setAgendarSiblings(siblings);
    setAgendarItemIds([item.id]); // pre-marca o clicado
    setAgendarAuditorIds(item.auditor_id ? [item.auditor_id] : []);
    // Permitir agendar QUALQUER auditor (nao apenas os vinculados ao hospital).
    // O principal (auditor do item) aparece PRIMEIRO e ja vem marcado/obrigatorio.
    const principalId = item.auditor_id;
    let lista = [...auditoresList];
    if (principalId && !lista.some(a => a.id === principalId)) {
      lista = [{ id: principalId, nome: item.auditor_nome || 'Principal' }, ...lista];
    }
    if (principalId) {
      lista = [
        ...lista.filter(a => a.id === principalId),
        ...lista.filter(a => a.id !== principalId),
      ];
    }
    setAgendarAuditoresHospital(lista);
  }, [data, auditoresList]);

  const toggleAgendarItem = (id: number) => {
    setAgendarItemIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleAgendarAuditor = (id: number) => {
    setAgendarAuditorIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  // Epic 46/47: Modal de intervalo de agendamento
  const [showIntervaloModal, setShowIntervaloModal] = useState(false);
  const [intervaloValidacoes, setIntervaloValidacoes] = useState<ValidarIntervaloResponse[]>([]);
  const [intervaloLoading, setIntervaloLoading] = useState(false);

  // Edit form
  const [editData, setEditData] = useState('');
  const [editInicio, setEditInicio] = useState('');
  const [editFim, setEditFim] = useState('');
  const [editando, setEditando] = useState(false);
  // N115: trocar/adicionar auditor no editar. A ORDEM importa — o backend usa
  // auditor_ids[0] como principal (operador do job).
  const [editAuditorIds, setEditAuditorIds] = useState<number[]>([]);
  const [editAuditoresOpts, setEditAuditoresOpts] = useState<{ id: number; nome: string }[]>([]);

  // Upload form
  const [uploadCliente, setUploadCliente] = useState<number | ''>('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  // Add hospital manual
  const [showAddHospitalModal, setShowAddHospitalModal] = useState(false);
  const [limpando, setLimpando] = useState(false);  // N82
  // N132: limpar tudo ou só um cliente
  const [showLimparModal, setShowLimparModal] = useState(false);
  const [limparEscopoCliente, setLimparEscopoCliente] = useState<number | ''>('');
  const [clientesDoEscopo, setClientesDoEscopo] = useState<DropdownOption[]>([]);
  const [addHospitalId, setAddHospitalId] = useState<number | ''>('');
  const [addClienteId, setAddClienteId] = useState<number | ''>('');
  const [addPontos, setAddPontos] = useState('');
  const [addingHospital, setAddingHospital] = useState(false);
  const [allHospitaisList, setAllHospitaisList] = useState<DropdownOption[]>([]);
  const [allClientesList, setAllClientesList] = useState<DropdownOption[]>([]);
  const [addAuditorNome, setAddAuditorNome] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (filterAuditor) params.auditor_id = String(filterAuditor);
      if (filterCliente) params.cliente_id = String(filterCliente);
      if (filterHospital) params.hospital_id = String(filterHospital);
      const result = await escopoContagemService.listarAgendamento(escopo.id, params);
      setData(result);
    } catch {
      msg.error('Erro ao carregar agendamento');
    } finally {
      setLoading(false);
    }
  }, [escopo.id, filterAuditor, filterCliente, filterHospital, refreshKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Volta o foco pra aba/janela -> recarrega sem clicar "Atualizar".
  useRefetchOnFocus(fetchData);

  // Load dropdowns
  useEffect(() => {
    const load = async () => {
      try {
        const [auds, clis, hosps] = await Promise.all([
          escopoContagemService.dropdownAuditores(),
          escopoContagemService.dropdownClientes(),
          escopoContagemService.dropdownHospitais(),
        ]);
        setAuditoresList(auds);
        setClientesList(clis);
        setHospitaisList(hosps);
      } catch { /* ignore */ }
    };
    load();
  }, []);

  // Filter by status card click
  //
  // ⛔ N179 (correção, 24/08): o card conta por BUCKET (o backend agrupa
  // CONFIRMADA/REAGENDADA em "Agendada" e CANCELADA/NAO_EXECUTADA/
  // NAO_AUTORIZADA em "Cancelada"), mas o filtro comparava status EXATO. O
  // card dizia 6 e a tabela mostrava 1 — e ninguém tinha percebido porque a
  // divergência só aparecia nos status raros. Agora os dois usam o mesmo mapa.
  const displayItems = useMemo(() => {
    if (!data) return [];
    if (!activeStatusFilter) return data.itens;
    return data.itens.filter(i => STATUS_BUCKET[i.status] === activeStatusFilter);
  }, [data, activeStatusFilter]);

  const handleStatusCardClick = (status: string) => {
    setActiveStatusFilter(prev => prev === status ? null : status);
  };

  // Agendar
  // Epic 46/47: valida intervalo antes. Se violacao, abre modal para pedir
  // justificativa. Se OK, segue direto.
  // cliente_ids dos itens marcados (via siblings)
  const selectedClienteIds = useMemo(
    () => agendarSiblings.filter(s => agendarItemIds.includes(s.id)).map(s => s.cliente_id),
    [agendarSiblings, agendarItemIds]
  );

  const handleAgendar = async () => {
    if (!agendarItem || !agendarData || agendarItemIds.length === 0) return;
    setAgendando(true);
    try {
      try {
        const validacao = await clienteService.validarIntervaloMultiplosClientes(
          selectedClienteIds.length > 0 ? selectedClienteIds : [agendarItem.cliente_id],
          agendarItem.hospital_id,
          agendarData,
        );
        if (!validacao.valido && validacao.validacoes?.some((v: any) => v.violacoes?.length > 0)) {
          setIntervaloValidacoes(validacao.validacoes);
          setShowIntervaloModal(true);
          setAgendando(false);
          return;
        }
      } catch (validationError: any) {
        console.warn('[AgendamentoTab] Erro na validacao de intervalo, seguindo com agendamento:', validationError);
      }
      await executarAgendar();
    } finally {
      setAgendando(false);
    }
  };

  // Executa de fato o POST /agendar (com ou sem justificativas)
  const executarAgendar = async (justificativas?: JustificativaIntervalo[]) => {
    if (!agendarItem || !agendarData) return;
    try {
      const payload: {
        data_prevista: string;
        hora_inicio: string;
        hora_fim: string;
        item_ids?: number[];
        auditor_ids?: number[];
        justificativas_intervalo?: JustificativaIntervalo[];
      } = {
        data_prevista: agendarData,
        hora_inicio: agendarInicio,
        hora_fim: agendarFim,
      };
      // Itens-irmaos marcados (alem do item da rota) e auditores marcados
      const extraItemIds = agendarItemIds.filter(id => id !== agendarItem.id);
      if (extraItemIds.length > 0) payload.item_ids = extraItemIds;
      if (agendarAuditorIds.length > 0) {
        // Garante que o principal (auditor do item) vá PRIMEIRO — o backend usa
        // auditor_ids[0] como operador/principal do job.
        const pid = agendarItem.auditor_id;
        payload.auditor_ids = (pid && agendarAuditorIds.includes(pid))
          ? [pid, ...agendarAuditorIds.filter(x => x !== pid)]
          : agendarAuditorIds;
      }
      if (justificativas && justificativas.length > 0) {
        payload.justificativas_intervalo = justificativas;
      }
      await escopoContagemService.agendarItem(escopo.id, agendarItem.id, payload);
      msg.success(
        justificativas && justificativas.length > 0
          ? 'Contagem agendada com justificativa de intervalo!'
          : 'Contagem agendada!'
      );
      setAgendarItem(null);
      fetchData();
      onDataChanged?.();
    } catch (err: any) {
      msg.error(err?.response?.data?.detail || 'Erro ao agendar');
      throw err;
    }
  };

  // Callbacks do modal de intervalo
  const handleIntervaloConfirmWithJustificativas = async (justificativas: JustificativaIntervalo[]) => {
    setIntervaloLoading(true);
    setAgendando(true);
    try {
      await executarAgendar(justificativas);
      setShowIntervaloModal(false);
      setIntervaloValidacoes([]);
    } catch {
      /* erro ja exibido em executarAgendar */
    } finally {
      setIntervaloLoading(false);
      setAgendando(false);
    }
  };

  const handleIntervaloCancel = () => {
    setShowIntervaloModal(false);
    setIntervaloValidacoes([]);
  };

  // Edit
  const openEditModal = (item: AgendamentoItem) => {
    setEditItem(item);
    setEditData(item.data_agendamento || '');
    setEditInicio(item.hora_inicio ? formatTime(item.hora_inicio) : '07:00');
    setEditFim(item.hora_fim ? formatTime(item.hora_fim) : '09:00');

    // N115: o auditor atual já vem marcado e em primeiro lugar (é o principal).
    // Se ele não estiver na lista geral de auditores (ex.: saiu do vínculo do
    // hospital), entra na frente de propósito — senão o editar mostraria o
    // agendamento sem auditor nenhum e a pessoa perderia a informação de quem
    // está lá hoje. Mesmo tratamento que o modal de Agendar faz.
    setEditAuditorIds(item.auditor_id ? [item.auditor_id] : []);
    let lista = [...auditoresList];
    if (item.auditor_id && !lista.some(a => a.id === item.auditor_id)) {
      lista = [{ id: item.auditor_id, nome: item.auditor_nome || 'Auditor atual' }, ...lista];
    }
    setEditAuditoresOpts(lista);
  };

  const toggleEditAuditor = (id: number) => {
    setEditAuditorIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id],
    );
  };

  const handleEdit = async () => {
    if (!editItem) return;
    // N115: agendamento não pode ficar sem auditor. O backend também recusa
    // lista vazia; avisar aqui evita a ida ao servidor para ouvir o mesmo.
    if (editAuditoresOpts.length > 0 && editAuditorIds.length === 0) {
      msg.error('Selecione ao menos um auditor.');
      return;
    }
    setEditando(true);
    try {
      const payload: Record<string, string | number[]> = {};
      if (editData) payload.data_prevista = editData;
      if (editInicio) payload.hora_inicio = editInicio;
      if (editFim) payload.hora_fim = editFim;
      // N115: só envia se realmente mudou. Mandar sempre faria uma edição de
      // data reescrever o auditor de TODOS os itens do job (o backend propaga
      // para os irmãos), gerando escrita e log sem motivo.
      const auditorMudou =
        editAuditorIds.length !== 1 || editAuditorIds[0] !== editItem.auditor_id;
      if (auditorMudou && editAuditorIds.length > 0) {
        payload.auditor_ids = editAuditorIds;
      }
      await escopoContagemService.editarItem(escopo.id, editItem.id, payload);
      msg.success('Agendamento atualizado!');
      setEditItem(null);
      fetchData();
      onDataChanged?.();
    } catch (err: any) {
      msg.error(err?.response?.data?.detail || 'Erro ao editar');
    } finally {
      setEditando(false);
    }
  };

  // Remove
  const handleRemove = async (item: AgendamentoItem) => {
    // N187: o texto dizia "O job será cancelado" — e desde o N179 ele APAGA.
    // "Cancelada" é a palavra que significa faturamento para a Alana; foi essa
    // confusão de linguagem que gerou o N179 e o reteste reprovado do N187.
    if (!confirm('Apagar este agendamento? A contagem sai do calendário e a vaga volta para "Não Agendada". Cancelar (que é cobrado) continua sendo pelo status na agenda.')) return;
    try {
      await escopoContagemService.removerItem(escopo.id, item.id);
      msg.success('Agendamento removido');
      fetchData();
      onDataChanged?.();
    } catch (err: any) {
      msg.error(err?.response?.data?.detail || 'Erro ao remover');
    }
  };

  // Remove manualmente o hospital (item) do escopo — exclui o EscopoItem.
  const handleExcluirItem = async (item: AgendamentoItem) => {
    if (!confirm(`Remover "${item.hospital_nome}" (${item.cliente_nome}) do escopo? Esta ação não pode ser desfeita.`)) return;
    try {
      await escopoContagemService.excluirItemEscopo(escopo.id, item.id);
      msg.success('Hospital removido do escopo');
      fetchData();
      onDataChanged?.();
    } catch (err: any) {
      msg.error(err?.response?.data?.detail || 'Erro ao remover do escopo');
    }
  };

  // Upload
  const handleUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    try {
      const clienteId = uploadCliente ? Number(uploadCliente) : null;
      const result = await escopoContagemService.uploadAgendamento(escopo.id, clienteId, uploadFile);
      setUploadResult(result);
      fetchData();
      onDataChanged?.();
    } catch (err: any) {
      msg.error(err?.response?.data?.detail || 'Erro no upload');
    } finally {
      setUploading(false);
    }
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setUploadFile(null);
    setUploadCliente('');
    setUploadResult(null);
  };

  // Add hospital manual
  const openAddHospitalModal = async () => {
    setAddHospitalId('');
    setAddClienteId('');
    setAddPontos('');
    setAddAuditorNome(null);
    setShowAddHospitalModal(true);
    try {
      const [h, c] = await Promise.all([
        escopoContagemService.dropdownHospitaisTodos(),
        escopoContagemService.dropdownClientesTodos(),
      ]);
      setAllHospitaisList(h);
      setAllClientesList(c);
    } catch { /* ignore */ }
  };

  const handleAddHospitalChange = async (hospitalId: number | '') => {
    setAddHospitalId(hospitalId);
    if (hospitalId) {
      try {
        const vinculos = await escopoContagemService.listarVinculos({ hospital_id: String(hospitalId) });
        setAddAuditorNome(vinculos.length > 0 ? vinculos[0].auditor_nome : null);
      } catch {
        setAddAuditorNome(null);
      }
    } else {
      setAddAuditorNome(null);
    }
  };

  const handleAddHospital = async () => {
    if (!addHospitalId || !addClienteId) return;
    setAddingHospital(true);
    try {
      const result = await escopoContagemService.addHospitalManual(escopo.id, {
        hospital_id: addHospitalId as number,
        cliente_id: addClienteId as number,
        pontos_contagem: addPontos || undefined,
      });
      if (result.success) {
        const auditorMsg = result.data?.auditor_nome
          ? ` (Auditor: ${result.data.auditor_nome})`
          : '';
        msg.success(`${result.message}${auditorMsg}`);
        setShowAddHospitalModal(false);
        fetchData();
        onDataChanged?.();
      } else {
        msg.error(result.message);
      }
    } catch (err: any) {
      msg.error(err?.response?.data?.message || 'Erro ao adicionar hospital');
    } finally {
      setAddingHospital(false);
    }
  };

  // N82: limpar escopo — destrutivo para as VAGAS, mas não cancela agendamentos.
  // N132 (Alana): agora dá para limpar só um cliente. O <select> lista apenas
  // quem tem vaga NESTE mês — oferecer cliente sem escopo aqui só gera engano.
  const openLimparModal = async () => {
    setLimparEscopoCliente('');
    setShowLimparModal(true);
    try {
      setClientesDoEscopo(await escopoContagemService.dropdownClientesDoEscopo(escopo.id));
    } catch {
      setClientesDoEscopo([]);
    }
  };

  const handleLimparEscopo = async () => {
    setLimpando(true);
    try {
      const result = await escopoContagemService.limparEscopo(
        escopo.id,
        limparEscopoCliente === '' ? undefined : limparEscopoCliente,
      );
      msg.success(result.message);
      setShowLimparModal(false);
      fetchData();
      onDataChanged?.();
    } catch (err: any) {
      msg.error(err?.response?.data?.detail || 'Erro ao limpar o escopo');
    } finally {
      setLimpando(false);
    }
  };

  // Export
  const handleExport = async () => {
    try {
      const blob = await escopoContagemService.exportAgendamento(escopo.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `agendamento_${escopo.mes}_${escopo.ano}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      msg.error('Erro ao exportar');
    }
  };

  // Template
  const handleTemplate = async () => {
    try {
      const blob = await escopoContagemService.templateAgendamento(escopo.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'template_escopo_agendamento.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      msg.error('Erro ao baixar template');
    }
  };

  // Min/max date for the agendar modal (restricted to escopo month)
  const minDate = `${escopo.ano}-${String(escopo.mes).padStart(2, '0')}-01`;
  const lastDay = new Date(escopo.ano, escopo.mes, 0).getDate();
  const maxDate = `${escopo.ano}-${String(escopo.mes).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

  const contadores = data?.contadores || { total: 0, nao_agendado: 0, pre_agendada: 0, agendada: 0, em_execucao: 0, executada: 0, nao_autorizada: 0, cancelada: 0 };

  const statusCards = [
    { key: null, label: 'Total', count: contadores.total, color: 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700' },
    { key: 'NAO_AGENDADO', label: 'Não Agendado', count: contadores.nao_agendado, color: 'bg-gray-50 dark:bg-gray-800 border-gray-300 dark:border-gray-600' },
    { key: 'PRE_AGENDADA', label: 'Pré-Agendada', count: contadores.pre_agendada, color: 'bg-yellow-50 dark:bg-yellow-900/10 border-yellow-300 dark:border-yellow-700' },
    { key: 'AGENDADA', label: 'Agendada', count: contadores.agendada, color: 'bg-blue-50 dark:bg-blue-900/10 border-blue-300 dark:border-blue-700' },
    { key: 'EXECUTADA', label: 'Executada', count: contadores.executada, color: 'bg-green-50 dark:bg-green-900/10 border-green-300 dark:border-green-700' },
    // ⛔ N179 (correção, 24/08). Antes eram 5 cards e nenhum era Cancelada —
    // não fazia falta porque a contagem cancelada era DESLINKADA da vaga e caía
    // em "Não Agendado". Agora ela mantém a vaga (a Alana fatura por status:
    // "uma cancelada eu cobro"), então sem este card o Total deixaria de bater
    // com a soma dos cards. O backend já mandava `contadores.cancelada`.
    // ⛔ N180 (Alana, 26/08). Dois cards novos, pela MESMA razão do de cima —
    // o Total tem de bater com a soma dos cards, porque é dessa soma que sai o
    // faturamento dela.
    //   • "Não Autorizada" saiu de dentro de "Cancelada": o card dizia 7 e eram
    //     6 não autorizadas + 1 cancelada. São negativas com significado
    //     comercial diferente (N21c).
    //   • "Em Execução" nunca teve card, e por isso a soma já não fechava antes
    //     desta correção: no print dela, 67 + 203 + 7 = 277 para um Total de
    //     278 — a contagem que faltava era a única EM_EXECUCAO.
    // NAO_EXECUTADA continua agrupada em "Cancelada" (era 0 no mês dela);
    // se ela quiser separar, é o mesmo movimento nos dois STATUS_BUCKET.
    { key: 'EM_EXECUCAO', label: 'Em Execução', count: contadores.em_execucao, color: 'bg-purple-50 dark:bg-purple-900/10 border-purple-300 dark:border-purple-700' },
    { key: 'NAO_AUTORIZADA', label: 'Não Autorizada', count: contadores.nao_autorizada, color: 'bg-orange-50 dark:bg-orange-900/10 border-orange-300 dark:border-orange-700' },
    { key: 'CANCELADA', label: 'Cancelada', count: contadores.cancelada, color: 'bg-red-50 dark:bg-red-900/10 border-red-300 dark:border-red-700' },
  ];

  const columns = useMemo<MRT_ColumnDef<AgendamentoItem>[]>(() => [
    { accessorKey: 'auditor_nome', header: 'Auditor', size: 150 },
    { accessorKey: 'cliente_nome', header: 'Cliente', size: 150 },
    // ⛔ N180 (Alana, item 7): "tem hospital que não está trazendo o código
    // cliente (ERP) mesmo estando cadastrado no sistema".
    //
    // Medido no banco: não é bug de exibição nem de match — são 17 vagas cujo
    // par hospital×cliente não tem código cadastrado. A célula vazia estava
    // certa; o que faltava era ela dizer POR QUE está vazia e o que preencher.
    // O backend agora manda null (sem vínculo) e '' (vínculo sem código) como
    // valores distintos.
    {
      accessorKey: 'codigo_erp',
      header: 'Cód. ERP',
      size: 100,
      Cell: ({ cell }) => {
        // `cell.getValue` aqui não é genérico tipado (TS2347), então o cast vem
        // depois — o mesmo padrão das outras colunas deste arquivo.
        const v = cell.getValue() as string | null;
        if (v) return <span>{v}</span>;
        const semVinculo = v === null || v === undefined;
        return (
          <span
            className="text-amber-700 dark:text-amber-500 cursor-help"
            title={semVinculo
              ? 'Sem vínculo entre este cliente e este hospital — cadastre o vínculo em Clientes › Hospitais.'
              : 'O vínculo existe, mas o Cód. ERP está em branco — preencha em Clientes › Hospitais.'}
          >
            {semVinculo ? 'sem vínculo' : 'sem código'}
          </span>
        );
      },
    },
    { accessorKey: 'hospital_nome', header: 'Hospital', size: 200 },
    { accessorKey: 'mes_ano', header: 'Mês', size: 80 },
    {
      accessorKey: 'hospital_agendado',
      header: 'Hosp. Agend.',
      size: 100,
      Cell: ({ cell }) => (
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cell.getValue() ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
          {cell.getValue() ? 'Sim' : 'Não'}
        </span>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 130,
      Cell: ({ cell }) => {
        const st = String(cell.getValue() || 'NAO_AGENDADO');
        const cfg = STATUS_CONFIG[st] || STATUS_CONFIG.NAO_AGENDADO;
        return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bgColor} ${cfg.color}`}>{cfg.label}</span>;
      },
    },
    {
      accessorKey: 'data_agendamento',
      header: 'Data',
      size: 90,
      Cell: ({ cell }) => <span>{formatDate(cell.getValue() as string | null)}</span>,
    },
    {
      id: 'acoes',
      header: 'Ações',
      size: 120,
      Cell: ({ row }) => {
        const item = row.original;
        const st = item.status;
        return (
          <div className="flex items-center gap-1">
            <button
              onClick={() => setHospitalConsulta(item)}
              className="text-slate-600 hover:text-blue-700 dark:text-slate-300 dark:hover:text-blue-300"
              title="Consultar hospital e pontos de contagem"
              aria-label={`Consultar hospital ${item.hospital_nome}`}
            >
              <Building2 className="w-4 h-4" />
            </button>
            {st === 'NAO_AGENDADO' && (
              <>
                <PermissionGate tela="ESCOPO_CONTAGEM_VIEW" acao="editar">
                  <button
                    onClick={() => openAgendarModal(item)}
                    className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Agendar
                  </button>
                </PermissionGate>
                <PermissionGate tela="ESCOPO_CONTAGEM_VIEW" acao="excluir">
                  <button
                    onClick={() => handleExcluirItem(item)}
                    className="text-red-600 hover:text-red-800 dark:text-red-400"
                    title="Remover hospital do escopo"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </PermissionGate>
              </>
            )}
            {(st === 'PRE_AGENDADA' || st === 'AGENDADA' || st === 'CONFIRMADA' || st === 'REAGENDADA') && (
              <>
                <button onClick={() => setDetalhesItem(item)} className="text-blue-600 hover:text-blue-800 dark:text-blue-400" title="Ver detalhes">
                  <Eye className="w-4 h-4" />
                </button>
                <PermissionGate tela="ESCOPO_CONTAGEM_VIEW" acao="editar">
                  <button onClick={() => openEditModal(item)} className="text-yellow-600 hover:text-yellow-800 dark:text-yellow-400" title="Editar">
                    <Edit3 className="w-4 h-4" />
                  </button>
                </PermissionGate>
                <PermissionGate tela="ESCOPO_CONTAGEM_VIEW" acao="editar">
                  <button onClick={() => handleRemove(item)} className="text-red-600 hover:text-red-800 dark:text-red-400" title="Desfazer agendamento">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </PermissionGate>
              </>
            )}
            {(st === 'EM_EXECUCAO' || st === 'EXECUTADA') && (
              <button onClick={() => setDetalhesItem(item)} className="text-blue-600 hover:text-blue-800 dark:text-blue-400" title="Ver detalhes">
                <Eye className="w-4 h-4" />
              </button>
            )}
            {/* ⛔ N179 (correção, 24/08) — a linha com desfecho NEGATIVO era um
                beco sem saída. Antes ela nunca chegava aqui (cancelada era
                deslinkada e virava "Não Agendado"); agora que mantém a vaga,
                CANCELADA/NAO_EXECUTADA/NAO_AUTORIZADA não caíam em ramo nenhum
                e a linha ficava sem Ver, sem Agendar e sem lixeira — a Alana
                perdia o hospital de vista.
                "Agendar" aqui é o reagendamento: o backend passou a aceitar
                vaga ocupada por contagem morta (`agendar_item`), e a contagem
                nova toma a vaga da cancelada. */}
            {(st === 'CANCELADA' || st === 'NAO_EXECUTADA' || st === 'NAO_AUTORIZADA') && (
              <>
                <button onClick={() => setDetalhesItem(item)} className="text-blue-600 hover:text-blue-800 dark:text-blue-400" title="Ver detalhes">
                  <Eye className="w-4 h-4" />
                </button>
                <PermissionGate tela="ESCOPO_CONTAGEM_VIEW" acao="editar">
                  <button
                    onClick={() => openAgendarModal(item)}
                    className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                    title="Reagendar este hospital no mês"
                  >
                    Reagendar
                  </button>
                </PermissionGate>
                <PermissionGate tela="ESCOPO_CONTAGEM_VIEW" acao="excluir">
                  <button
                    onClick={() => handleExcluirItem(item)}
                    className="text-red-600 hover:text-red-800 dark:text-red-400"
                    title="Remover hospital do escopo"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </PermissionGate>
              </>
            )}
          </div>
        );
      },
    },
  ], [openAgendarModal]);

  const table = useMantineReactTable({
    columns,
    data: displayItems,
    enableColumnFilters: true,
    enableSorting: true,
    enablePagination: true,
    enableDensityToggle: false,
    initialState: { density: 'xs', pagination: { pageIndex: 0, pageSize: 25 } },
    state: { isLoading: loading },
    mantineTableProps: {
      highlightOnHover: true,
      striped: true,
      withTableBorder: true,
      withColumnBorders: true,
    },
    mantinePaperProps: { shadow: 'none', withBorder: false },
  });

  return (
    <div>
      {/* Status Cards */}
      {/* N179: 6 cards — entrou "Cancelada" (ver statusCards). */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
        {statusCards.map((card) => (
          <button
            key={card.key || 'total'}
            onClick={() => card.key ? handleStatusCardClick(card.key) : setActiveStatusFilter(null)}
            className={`p-3 rounded-lg border-2 text-center transition-all cursor-pointer ${card.color} ${
              activeStatusFilter === card.key ? 'ring-2 ring-blue-500 ring-offset-1' : ''
            } ${!card.key && !activeStatusFilter ? 'ring-2 ring-blue-500 ring-offset-1' : ''}`}
          >
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{card.count}</div>
            <div className="text-xs text-gray-600 dark:text-gray-400">{card.label}</div>
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {/* Filters */}
        <select
          value={filterAuditor}
          onChange={e => setFilterAuditor(e.target.value ? Number(e.target.value) : '')}
          className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        >
          <option value="">Todos Auditores</option>
          {auditoresList.map(a => <option key={a.id} value={a.id}>{a.nome}</option>)}
        </select>
        <select
          value={filterCliente}
          onChange={e => setFilterCliente(e.target.value ? Number(e.target.value) : '')}
          className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        >
          <option value="">Todos Clientes</option>
          {clientesList.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
        </select>
        <select
          value={filterHospital}
          onChange={e => setFilterHospital(e.target.value ? Number(e.target.value) : '')}
          className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        >
          <option value="">Todos Hospitais</option>
          {hospitaisList.map(h => <option key={h.id} value={h.id}>{h.nome}</option>)}
        </select>

        <div className="flex-1" />

        {isMaster && (
          <>
          {/* N82 (Alana): "Adicionar hospital" reativado (era só upload) e
              "Limpar escopo" para zerar antes de uma carga nova. */}
          <div className="flex items-center gap-1">
            <button
              onClick={openAddHospitalModal}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-1.5"
            >
              <Plus className="w-4 h-4" /> Adicionar Hospital
            </button>
            <button
              onClick={openLimparModal}
              disabled={limpando}
              className="px-3 py-1.5 text-sm border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-1.5 disabled:opacity-50"
              title="Zera o escopo do mês (os agendamentos NÃO são cancelados)"
            >
              {limpando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Limpar Escopo
            </button>
            <button
              onClick={() => { setUploadFile(null); setUploadCliente(''); setShowUploadModal(true); }}
              className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md flex items-center gap-1.5"
            >
              <Upload className="w-4 h-4" /> Upload Escopo
            </button>
            <button
              onClick={() => setShowInfoModal(true)}
              className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
              title="Info template"
            >
              <Info className="w-4 h-4" />
            </button>
          </div>
          </>
        )}
        <PermissionGate tela="ESCOPO_CONTAGEM_VIEW" acao="exportar">
          <div className="flex items-center gap-1">
            <button
              onClick={handleExport}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 flex items-center gap-1.5"
            >
              <Download className="w-4 h-4" /> Exportar
            </button>
            <span title="Exporta todos os itens do escopo com status atual">
              <Info className="w-4 h-4 text-gray-400 hover:text-blue-500 cursor-help" />
            </span>
          </div>
        </PermissionGate>
      </div>

      {/* Table */}
      <MantineReactTable table={table} />

      {hospitalConsulta && (
        <HospitalConsultaModal
          hospitalId={hospitalConsulta.hospital_id}
          clienteId={hospitalConsulta.cliente_id}
          onClose={() => setHospitalConsulta(null)}
        />
      )}

      {/* Agendar Modal */}
      {agendarItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" /> Agendar Contagem
              </h3>
              <button onClick={() => setAgendarItem(null)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="space-y-3 text-sm">
              <div className="text-gray-600 dark:text-gray-400">
                <p><strong>Hospital:</strong> {agendarItem.hospital_nome}</p>
              </div>

              {/* Clientes do hospital (checkbox) — agenda varios na MESMA contagem */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Clientes <span className="text-xs text-gray-400 font-normal">(marque um ou mais do mesmo hospital)</span>
                </label>
                <div className="max-h-32 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-md p-2 space-y-1">
                  {agendarSiblings.map(s => (
                    <label key={s.id} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={agendarItemIds.includes(s.id)}
                        onChange={() => toggleAgendarItem(s.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      {s.cliente_nome}
                    </label>
                  ))}
                </div>
              </div>

              {/* Auditores vinculados ao hospital (checkbox) — varios por contagem */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Auditores <span className="text-xs text-gray-400 font-normal">(um ou mais)</span>
                </label>
                <div className="max-h-32 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-md p-2 space-y-1">
                  {agendarAuditoresHospital.length === 0 && (
                    <p className="text-xs text-gray-400">Nenhum auditor disponivel</p>
                  )}
                  {agendarAuditoresHospital.map(a => {
                    const isPrincipal = agendarItem?.auditor_id === a.id;
                    return (
                      <label key={a.id} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={agendarAuditorIds.includes(a.id)}
                          disabled={isPrincipal}
                          onChange={() => toggleAgendarAuditor(a.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        {a.nome}
                        {isPrincipal && (
                          <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">(principal)</span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Data</label>
                <input
                  type="date"
                  value={agendarData}
                  onChange={e => setAgendarData(e.target.value)}
                  min={minDate}
                  max={maxDate}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
                />
                <p className="text-xs text-gray-500 mt-1">Restrito ao mês {String(escopo.mes).padStart(2, '0')}/{escopo.ano}</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Início</label>
                  <input type="time" value={agendarInicio} onChange={e => setAgendarInicio(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Fim</label>
                  <input type="time" value={agendarFim} onChange={e => setAgendarFim(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white" />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setAgendarItem(null)} className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300">Cancelar</button>
              <button onClick={handleAgendar} disabled={!agendarData || agendarItemIds.length === 0 || agendando} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400">
                {agendando ? 'Agendando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ver Detalhes Modal */}
      {detalhesItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Detalhes do Agendamento</h3>
              <button onClick={() => setDetalhesItem(null)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
              <p><strong>Hospital:</strong> {detalhesItem.hospital_nome}</p>
              <p><strong>Cliente:</strong> {detalhesItem.cliente_nome}</p>
              <p><strong>Cód. ERP:</strong> {detalhesItem.codigo_erp
                ? detalhesItem.codigo_erp
                : (detalhesItem.codigo_erp === ''
                    ? 'vínculo sem código cadastrado'
                    : 'sem vínculo cliente × hospital')}</p>
              <p><strong>Auditor:</strong> {detalhesItem.auditor_nome || '—'}</p>
              <p><strong>Data:</strong> {formatDate(detalhesItem.data_agendamento)}</p>
              <p><strong>Horário:</strong> {formatTime(detalhesItem.hora_inicio)} - {formatTime(detalhesItem.hora_fim)}</p>
              <p><strong>Status:</strong> <span className={STATUS_CONFIG[detalhesItem.status]?.color || ''}>{STATUS_CONFIG[detalhesItem.status]?.label || detalhesItem.status}</span></p>
              {detalhesItem.pontos_contagem && <p><strong>Pontos:</strong> {detalhesItem.pontos_contagem}</p>}
            </div>
            <div className="flex justify-end mt-6">
              <button onClick={() => setDetalhesItem(null)} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700">Fechar</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Editar Agendamento</h3>
              <button onClick={() => setEditItem(null)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              <p>{editItem.hospital_nome} / {editItem.cliente_nome}</p>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Data</label>
                <input type="date" value={editData} onChange={e => setEditData(e.target.value)} min={minDate} max={maxDate} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Início</label>
                  <input type="time" value={editInicio} onChange={e => setEditInicio(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Fim</label>
                  <input type="time" value={editFim} onChange={e => setEditFim(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white" />
                </div>
              </div>

              {/* N115 (Alana/Otávio): trocar ou adicionar auditor sem desfazer o
                  agendamento — "se eu subir errado o escopo, consigo trocar o
                  auditor". Diferente do modal de Agendar, aqui NENHUM item fica
                  travado: o principal é justamente o que precisa poder mudar. */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Auditores <span className="text-xs text-gray-400 font-normal">(um ou mais)</span>
                </label>
                <div className="max-h-32 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-md p-2 space-y-1">
                  {editAuditoresOpts.length === 0 && (
                    <p className="text-xs text-gray-400">Nenhum auditor disponível</p>
                  )}
                  {editAuditoresOpts.map(a => (
                    <label key={a.id} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editAuditorIds.includes(a.id)}
                        onChange={() => toggleEditAuditor(a.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      {a.nome}
                      {editAuditorIds[0] === a.id && (
                        <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">(principal)</span>
                      )}
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  O primeiro marcado é o principal. Para trocar quem é o
                  principal, desmarque e marque na ordem desejada.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setEditItem(null)} className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300">Cancelar</button>
              <button onClick={handleEdit} disabled={editando} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400">
                {editando ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Upload Escopo</h3>
              <button onClick={closeUploadModal}><X className="w-5 h-5 text-gray-400" /></button>
            </div>

            {uploadResult ? (
              /* ---- Upload Result Screen ---- */
              <div className="space-y-4">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  <p className="font-medium">Resultado do upload:</p>
                  <p className="mt-1">{uploadResult.novos} novos, {uploadResult.substituidos} substituídos de {uploadResult.total_linhas} linhas processadas</p>
                </div>

                {uploadResult.errors && uploadResult.errors.length > 0 ? (
                  <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-md">
                    <p className="text-sm font-medium text-amber-700 dark:text-amber-400 mb-2">
                      {uploadResult.errors.length} {uploadResult.errors.length === 1 ? 'linha não foi importada' : 'linhas não foram importadas'}:
                    </p>
                    <ul className="max-h-60 overflow-y-auto space-y-1 text-xs text-amber-700 dark:text-amber-400 select-text">
                      {uploadResult.errors.map((err, idx) => (
                        <li key={idx} className="py-0.5 border-b border-amber-100 dark:border-amber-800 last:border-0">{err}</li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-md">
                    <p className="text-sm font-medium text-green-700 dark:text-green-400">Todas as linhas foram importadas com sucesso!</p>
                  </div>
                )}

                <div className="flex justify-end gap-2 mt-4">
                  <button
                    onClick={() => { setUploadResult(null); setUploadFile(null); }}
                    className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Fazer novo upload
                  </button>
                  <button onClick={closeUploadModal} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700">
                    Fechar
                  </button>
                </div>
              </div>
            ) : (
              /* ---- Upload Form Screen ---- */
              <>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Cliente <span className="text-xs text-gray-400 font-normal">(aplica a todas as linhas do Excel)</span>
                    </label>
                    <select
                      value={uploadCliente}
                      onChange={e => setUploadCliente(e.target.value ? Number(e.target.value) : '')}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
                    >
                      <option value="">Usar CNPJ Cliente do Excel</option>
                      {clientesList.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Arquivo Excel</label>
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={e => setUploadFile(e.target.files?.[0] || null)}
                      className="w-full text-sm text-gray-900 dark:text-gray-100"
                    />
                  </div>
                  <button onClick={handleTemplate} className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 underline">
                    Baixar template
                  </button>
                  <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-md text-xs text-amber-700 dark:text-amber-400">
                    Se já existir escopo para este cliente/hospital neste mês, os agendamentos PRE_AGENDADA e AGENDADA serão cancelados e substituídos. Jobs EM_EXECUCAO e EXECUTADA não serão alterados.
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-6">
                  <button onClick={closeUploadModal} className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300">Cancelar</button>
                  <button onClick={handleUpload} disabled={!uploadFile || uploading} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400">
                    {uploading ? 'Enviando...' : 'Enviar'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Limpar Escopo Modal (N132) — era um window.confirm de "limpa tudo" */}
      {showLimparModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-red-600" /> Limpar Escopo
              </h3>
              <button onClick={() => setShowLimparModal(false)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>

            <div className="space-y-3 text-sm">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  className="mt-1"
                  checked={limparEscopoCliente === ''}
                  onChange={() => setLimparEscopoCliente('')}
                />
                <span className="text-gray-900 dark:text-white">
                  Todo o escopo do mês
                  <span className="block text-xs text-gray-500 dark:text-gray-400">
                    Apaga as vagas de todos os clientes.
                  </span>
                </span>
              </label>

              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  className="mt-1"
                  checked={limparEscopoCliente !== ''}
                  onChange={() => setLimparEscopoCliente(clientesDoEscopo[0]?.id ?? '')}
                  disabled={clientesDoEscopo.length === 0}
                />
                <span className="text-gray-900 dark:text-white">
                  Apenas um cliente
                  <span className="block text-xs text-gray-500 dark:text-gray-400">
                    Os outros clientes deste mês ficam intactos.
                  </span>
                </span>
              </label>

              {limparEscopoCliente !== '' && (
                <select
                  value={limparEscopoCliente}
                  onChange={e => setLimparEscopoCliente(e.target.value ? Number(e.target.value) : '')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
                >
                  {clientesDoEscopo.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
              )}

              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded text-xs text-amber-800 dark:text-amber-300 space-y-1">
                <p>• As vagas do escopo serão apagadas.</p>
                <p>• As contagens JÁ AGENDADAS <strong>não são canceladas</strong>: continuam ativas e passam a aparecer em "Fora do Escopo".</p>
                <p>• Ao subir a nova carga, os hospitais que voltarem ao escopo são re-vinculados automaticamente.</p>
                <p>• Contagens em execução ou já executadas são mantidas no escopo.</p>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowLimparModal(false)} className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300">Cancelar</button>
              <button
                onClick={handleLimparEscopo}
                disabled={limpando}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400 flex items-center gap-1.5"
              >
                {limpando && <Loader2 className="w-4 h-4 animate-spin" />}
                {limpando ? 'Limpando...' : 'Limpar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Hospital Manual Modal */}
      {showAddHospitalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Plus className="w-5 h-5 text-green-600" /> Adicionar Hospital ao Escopo
              </h3>
              <button onClick={() => setShowAddHospitalModal(false)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="space-y-3 text-sm">
              <div>
                {/* N133 (Alana): era um <select>, que não tem busca — com a lista
                    inteira de hospitais, achar um pelo nome exato era o único
                    caminho. Agora busca por nome, código ou CNPJ (com e sem
                    pontuação), igual ao modal "Vincular" do N114. */}
                <ComboboxSelector
                  label="Hospital *"
                  options={opcoesHospitalBuscavel(
                    allHospitaisList.map(h => ({
                      id: h.id, nome: h.nome, codigo: h.codigo, cnpj: h.cnpj,
                    })),
                  )}
                  value={addHospitalId === '' ? 0 : addHospitalId}
                  onChange={(id) => handleAddHospitalChange(id || '')}
                  placeholder="Selecione o hospital..."
                  searchPlaceholder="Buscar por nome, código ou CNPJ..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cliente *</label>
                <select
                  value={addClienteId}
                  onChange={e => setAddClienteId(e.target.value ? Number(e.target.value) : '')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
                >
                  <option value="">Selecione o cliente...</option>
                  {allClientesList.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
              </div>
              {addAuditorNome && (
                <div className="p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded text-xs text-blue-700 dark:text-blue-400">
                  Auditor vinculado: <strong>{addAuditorNome}</strong>
                </div>
              )}
              {addHospitalId && !addAuditorNome && (
                <div className="p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded text-xs text-amber-700 dark:text-amber-400">
                  Nenhum auditor vinculado a este hospital. O item será criado sem auditor.
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Pontos de Contagem</label>
                <input
                  type="text"
                  value={addPontos}
                  onChange={e => setAddPontos(e.target.value)}
                  placeholder="Ex: Opme / Hemodinâmica / Almoxarifado"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowAddHospitalModal(false)} className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300">Cancelar</button>
              <button
                onClick={handleAddHospital}
                disabled={!addHospitalId || !addClienteId || addingHospital}
                className="px-4 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400"
              >
                {addingHospital ? 'Adicionando...' : 'Adicionar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Info Modal - Upload Escopo */}
      {showInfoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Formato do Template - Escopo</h3>
              <button onClick={() => setShowInfoModal(false)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
              <p>O template Excel deve conter as colunas:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>CNPJ Hospital *</strong> — CNPJ do hospital (com ou sem formatacao)</li>
                <li><strong>CNPJ Cliente *</strong> — CNPJ do cliente (obrigatorio)</li>
              </ul>
              <div className="mt-3 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded text-xs text-blue-700 dark:text-blue-400 space-y-1">
                <p>O auditor preferencial e atribuido automaticamente a partir dos vinculos cadastrados na Tab 1 (Vinculos).</p>
                <p>Se ja existir escopo, agendamentos PRE_AGENDADA/AGENDADA serao substituidos. Jobs EM_EXECUCAO e EXECUTADA nao sao alterados.</p>
              </div>
            </div>
            <div className="flex justify-end mt-6">
              <button onClick={() => setShowInfoModal(false)} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700">Fechar</button>
            </div>
          </div>
        </div>
      )}

      {/* Epic 46/47: Modal de violacao de intervalo de agendamento */}
      {showIntervaloModal && (
        <IntervaloAgendamentoModal
          isOpen={showIntervaloModal}
          onClose={handleIntervaloCancel}
          onConfirm={() => { /* sem justificativas nao eh usado aqui */ }}
          onConfirmWithJustificativas={handleIntervaloConfirmWithJustificativas}
          validacoes={intervaloValidacoes}
          dataSelecionada={agendarData}
          loading={intervaloLoading}
        />
      )}
    </div>
  );
}
